'use client';

import { map as _map, filter as _filter, partition as _partition } from 'lodash-es';
import { Fragment, useMemo, useState } from 'react';
import Link from 'next/link';
import { useQuery } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { makeStyles } from 'tss-react/mui';
import Loading from '@/components/loading';
import ErrorMessage from '@/components/error-message';
import { eventSeriesIndexQuery } from '@/apollo/event-series';
import Main from '@/components/main';
import { useAppContext } from '@/contexts/app';
import MaterialLinkButton from '@/components/material-link-button';
import LoginRequiredNotice from '@/components/login-required-notice';
import EventSeriesList from '@/components/event-series-list';
import SectionHeader from '@/components/section-header';

const useStyles = makeStyles()((theme) => ({
  createButton: {
    marginTop: theme.spacing(1),
  },
}));

export default function EventSeriesIndexPageClient() {
  const { classes } = useStyles();
  const { isLoggedIn } = useAppContext();

  const [showCancelled, setShowCancelled] = useState(false);
  const { data, loading, error } = useQuery(eventSeriesIndexQuery, {
    skip: !isLoggedIn,
    ssr: false,
  });
  const { myActiveSeries, myGroups, cancelledSeries } = useMemo(() => {
    if (!data) {
      return {
        myActiveSeries: [],
        myGroups: [],
        cancelledSeries: [],
      };
    }

    const [active, cancelled] = _partition(
      data.me.eventSeries,
      (s) => s.status !== 'CANCELLED'
    );

    const myGroups = _filter(data.me.groups, (group) => {
      return group.eventSeries.some((s) => s.status !== 'CANCELLED');
    });

    return {
      myActiveSeries: active,
      myGroups,
      cancelledSeries: cancelled,
    };
  }, [data]);

  if (error) {
    return (
      <ErrorMessage
        message='An error occurred while loading event series.'
        error={error}
      />
    );
  }
  if (loading) { return <Loading />; }

  return (
    <Main compact>
      <Typography variant="h1">
        Event Series
      </Typography>
      {!isLoggedIn && (
        <LoginRequiredNotice
          returnTo="/event-series"
          action="view your event series"
        />
      )}
      <p>To start a new series, create an event, then from that event click &ldquo;Duplicate &gt; Convert to Series&rdquo; to convert your first event to a series and schedule upcoming events.</p>
      {myActiveSeries.length > 0 && (
        <Fragment>
          <SectionHeader>My Series</SectionHeader>
          <EventSeriesList list={myActiveSeries} />
        </Fragment>
      )}
      {myGroups.length > 0 && (
        <Fragment>
          {_map(myGroups, (group) => {
            const activeSeries = _filter(group.eventSeries, (s) => s.status !== 'CANCELLED');
            return (
              <Fragment key={group.id}>
                <SectionHeader>
                  <Link
                    href={`/groups/${group.slug}`}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {group.name}
                  </Link>
                </SectionHeader>
                <EventSeriesList list={activeSeries} />
              </Fragment>
            );
          })}
        </Fragment>
      )}
      {cancelledSeries.length > 0 && (
        <Fragment>
          <Button
            size="small"
            onClick={() => setShowCancelled((prev) => !prev)}
            sx={{ mt: 2 }}
          >
            {showCancelled ? 'Hide' : 'Show'} cancelled series ({cancelledSeries.length})
          </Button>
          {showCancelled && (
            <EventSeriesList list={cancelledSeries} />
          )}
        </Fragment>
      )}
      <MaterialLinkButton
        href="/event-series/new"
        variant="contained"
        color="primary"
        className={classes.createButton}
      >
        Create Event Series
      </MaterialLinkButton>
    </Main>
  );
}
